/**
 * SD-8.2 (UX-DR15, §C.3): what a typed setback value becomes when the inline editor commits.
 *
 * Pure and separate from the component so the parsing rules are testable without a DOM and the
 * component cannot quietly grow its own. Three outcomes, per the spec's table:
 *
 *  - a finite number in range commits as typed;
 *  - a finite number out of range commits CLAMPED to 0–48 and carries `clamped: true`, because
 *    900 silently becoming 48 is mysterious and 900 explained is not;
 *  - anything else reverts, SILENTLY — the caller restores the pre-edit value (not the default;
 *    the spec is explicit that Esc and invalid input go back to what was there before the editor
 *    opened, and only the caller knows that value).
 *
 * The unit is rendered, never typed and never parsed: "18 in" is non-numeric here on purpose,
 * so no unit-stripping regex can ever creep in.
 */

export const SETBACK_MIN_IN = 0;
export const SETBACK_MAX_IN = 48;

export type InlineEditResolution =
  | { kind: 'commit'; value: number; typed: number; clamped: boolean }
  | { kind: 'revert' };

export function resolveInlineEdit(
  raw: string,
  min: number = SETBACK_MIN_IN,
  max: number = SETBACK_MAX_IN
): InlineEditResolution {
  const trimmed = raw.trim();
  if (trimmed === '') return { kind: 'revert' };
  const typed = Number(trimmed);
  if (!Number.isFinite(typed)) return { kind: 'revert' };
  const value = Math.min(max, Math.max(min, typed));
  return { kind: 'commit', value, typed, clamped: value !== typed };
}

/** The §C.3 "says it clamped" string; null whenever nothing was clamped. */
export function clampNote(
  resolution: InlineEditResolution,
  min: number = SETBACK_MIN_IN,
  max: number = SETBACK_MAX_IN
): string | null {
  if (resolution.kind !== 'commit' || !resolution.clamped) return null;
  return `${resolution.typed} is outside ${min}–${max} in, so it was set to ${resolution.value}.`;
}
